// BOM module's data client.
//
// Ported from the standalone Covaris BOM Viewer, where this file called Orcanos directly
// from the browser with a `Basic user:password` header kept in localStorage, and composed
// the SQL `Filter_By` clauses itself. Both are gone:
//
//   * Every call goes to this platform's own `/api/bom/*` routes, authenticated by the
//     httpOnly session cookie. The browser never holds an Orcanos credential.
//   * The page sends INTENT (a view, a search string, a numeric id). The server builds the
//     SQL (`bom_api.py`) — a clause accepted from the browser would be arbitrary SQL
//     appended to a saved filter's WHERE.
//
// The exported function names and return shapes are unchanged, so the tree code that
// calls them is the standalone app's, nearly verbatim.

import { getSettings, versionForType } from '../settings/settingsStore';
import { getCsrfToken } from '../../utils/auth';

export class OrcanosError extends Error {
  constructor(httpCode, message) {
    super(message || `Orcanos error ${httpCode}`);
    this.httpCode = httpCode;
  }
}

/**
 * Fetch top-level rows — the BOM filter, or the Part Catalog when `view === 'parts'`.
 * `whereUsedId` narrows the result to the BOMs that contain that part.
 *
 *   { rows, total }   on success. Throws `OrcanosError` on failure.
 */
export async function fetchBoms({
  view = 'boms',
  page = 1,
  pageSize = 100,
  searchQuery = null,
  whereUsedId = null,
} = {}) {
  const data = await _post('roots', {
    view,
    page,
    page_size: pageSize,
    search: searchQuery || '',
    where_used_id: whereUsedId || null,
  });
  return _rows(data);
}

/**
 * The numeric part of an Orcanos key typed into the search box, or '' when the
 * query doesn't look like a key or id.
 *
 *   "39382"            → "39382"   (bare id, or part of one)
 *   "PRT-393"          → "393"     (key, or the start of one)
 *   "PRT-39382-901631" → "39382"   (Master Part Source shape: PREFIX-id-code)
 *   "520377KIT"        → ""        (a code — matched on Obj_name only)
 *
 * ⚠️ Mirrored by `search_id_digits` in bom_api.py, which builds the server search clause.
 * The tree uses this copy to filter the loaded page client-side — change both together,
 * or the server returns a row that the page then hides.
 */
export function searchIdDigits(query) {
  const q = String(query ?? '').trim();
  const key = q.match(/^[A-Za-z][A-Za-z0-9_]*-(\d+)(?:-|$)/);
  if (key) return key[1];
  return /^\d+$/.test(q) ? q : '';
}

/**
 * The ECOs (Change Requests) that reference a part or part instance.
 * PRT rows are looked up by their own id; PI rows by their CS21 integer.
 */
export async function fetchRelatedEcos({ row }) {
  const id = row.type === 'PI' ? row.cs21Int : row.itemId;
  const data = await _post('ecos', { item_id: String(id ?? '') });
  return _rows(data);
}

/**
 * The children of a BOM/Assembly, given the parent's original id.
 *
 *   { rows, total }   on success. Throws `OrcanosError` on failure.
 */
export async function fetchChildren({
  parentOriginalId,
  page = 1,
  pageSize = 100,
} = {}) {
  if (parentOriginalId === undefined || parentOriginalId === null || parentOriginalId === '') {
    throw new OrcanosError(0, 'parentOriginalId is required');
  }
  // Assembly (Admin → Settings): a node's children are SUB-ASSEMBLIES and PARTS — two
  // work item types, fetched together by the server so one expand is one request.
  // Checked before part hierarchy, the same precedence the server applies (`_structure`).
  if (getSettings().useAssembly) {
    return fetchAssemblyChildren({ parentOriginalId, page, pageSize });
  }
  // Part hierarchy (Admin → Settings): a part's children are PARTS, not part instances.
  // Its own route — the part-instance path below is left exactly as it was.
  if (getSettings().usePartHierarchy) {
    return fetchPartChildren({ parentOriginalId, page, pageSize });
  }
  // Part instances switched off in Admin → Settings: every BOM is a leaf, so don't ask.
  if (getSettings().usePartInstance === false) return { rows: [], total: 0 };
  const data = await _post('children', {
    parent_original_id: String(parentOriginalId),
    page,
    page_size: pageSize,
  });
  return _rows(data);
}

/**
 * Part hierarchy: the parts whose Orcanos parent is `parentOriginalId`, read by the server
 * from the Part Catalog filter. Rows are PRT, so their own id is what expands them further.
 */
export async function fetchPartChildren({ parentOriginalId, page = 1, pageSize } = {}) {
  const data = await _post('part-children', {
    parent_original_id: String(parentOriginalId),
    page,
    page_size: pageSize || null,
  });
  return _rows(data);
}

/**
 * Assembly structure: one node's children — its sub-assemblies AND its parts, in that
 * order, already merged by the server into a single page. Rows carry their own `type`, so
 * a sub-assembly expands further and a part is drawn as a leaf by the same probe as
 * everywhere else.
 */
export async function fetchAssemblyChildren({ parentOriginalId, page = 1, pageSize } = {}) {
  const data = await _post('assembly-children', {
    parent_original_id: String(parentOriginalId),
    page,
    page_size: pageSize || null,
  });
  return _rows(data);
}

/**
 * The parts and DMS items an ECO lists. Each DMS row's `ecoFile` is the revision that ECO
 * released — `{ attachmentId, name, revision, changeNo, url }` — or null; `ecoFileError` is
 * set when its revisions could not be read.
 */
export async function fetchEcoChildren({ ecoId } = {}) {
  if (!ecoId) throw new OrcanosError(0, 'ecoId is required');
  const data = await _post('eco-children', { eco_id: String(ecoId) });
  // `unread`: the lists (Part Catalog / DMS items) whose filter is not set, so not read.
  return { ..._rows(data), unread: Array.isArray(data?.unread) ? data.unread : [] };
}

/**
 * Which ECOs list at least one item the page could show: `{ [ecoId]: boolean }`. An ECO the
 * server could not check is missing from the answer (keep its arrow).
 */
export async function fetchEcoHasItems(ecoIds) {
  const data = await _post('eco-has-items', { eco_ids: ecoIds.map(String) });
  return data?.has_items || {};
}

/** Assembly structure: which top-level rows have children — originalId -> boolean. A row the
 *  check could not answer is missing from the answer. */
export async function fetchAssemblyHasChildren(parentIds) {
  const data = await _post('assembly-has-children', { parent_ids: parentIds.map(String) });
  return data?.has_children || {};
}

/**
 * The children of a row IN A GIVEN VIEW — the one place that decides what "expand" means:
 *   boms / parts → whichever structure this tenant uses: the Part Instance filter (a BOM's
 *                  instances), the Part Catalog filter (a part's child parts), or the
 *                  assembly structure's sub-assemblies + parts
 *   ecos         → an ECO (CR) expands into its parts and DMS items; those are leaves
 *   dms          → every row is a leaf
 *
 * A DMS row is a leaf in EVERY view: a document used as a part hangs off an assembly, it
 * never holds parts of its own — so it is not probed, in any structure.
 */
export function fetchChildRows(view, row, { pageSize } = {}) {
  if (view === 'dms' || row?.type === 'DMS') return Promise.resolve({ rows: [], total: 0 });
  if (view === 'ecos') {
    if (row?.type !== 'CR') return Promise.resolve({ rows: [], total: 0 });
    return fetchEcoChildren({ ecoId: row.itemId });
  }
  // The Assembly list always expands through the assembly sources — also when it is shown
  // beside Product structures under another BOM structure (user, 2026-11-02).
  if (view === 'assemblies') return fetchAssemblyChildren({ parentOriginalId: row?.originalId, pageSize });
  return fetchChildren({ parentOriginalId: row?.originalId, pageSize });
}

/** Views whose top-level rows never expand. */
export function isLeafView(view) {
  return view === 'dms';
}

/**
 * Export an ECO as a zip: `html` (index.html, with `__ECOFILE_HREF_<id>__` /
 * `__ECOFILE_NAME_<id>__` tokens where file links go) plus the released files, which the
 * server resolves from the ECO id itself. `includeAttachments` adds the plain attachments
 * of each DMS item it lists (not the document file itself), also resolved server-side. Resolves to a Blob.
 */
export function exportEcoZip({ ecoId, html, table, indexFormat, name, includeAttachments = true }) {
  return postZip('/api/bom/eco-export', {
    eco_id: String(ecoId),
    include_attachments: !!includeAttachments,
    html: html || '',
    table: table || null,
    index_format: indexFormat || 'html',
    name: name || '',
  });
}

/**
 * Export a BOM / assembly as a zip: the same `index.html` plus the current file of every DMS
 * item in the tree. `itemIds` are the DMS rows the page drew — a NARROWING, not a permission:
 * the server re-checks each one against the account's DMS filter and picks the revision
 * itself. `attachments` are the other rows ({ id, type, version, master_id }) whose plain
 * attachments (STEP models, drawings) go in the package — the server lists them itself.
 * `indexFormat` html sends `html`; json / csv / xlsx / pdf send `table` and the server
 * renders the index. Resolves to a Blob.
 */
export function exportBomZip({ itemIds, attachments, includeAttachments = true, html, table, indexFormat, name }) {
  return postZip('/api/bom/bom-export', {
    item_ids: (itemIds || []).map(String),
    attachments: attachments || [],
    include_attachments: !!includeAttachments,
    html: html || '',
    table: table || null,
    index_format: indexFormat || 'html',
    name: name || '',
  });
}

async function postZip(url, body) {
  let resp;
  try {
    resp = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new OrcanosError(0, `Could not reach the server (${err.message}).`);
  }
  if (!resp.ok) {
    let detail = null;
    try { detail = (await resp.json()).detail; } catch { /* non-JSON */ }
    throw new OrcanosError(resp.status, typeof detail === 'string' ? detail : `Export failed (HTTP ${resp.status}).`);
  }
  return resp.blob();
}

/**
 * A work item's change log (the History tab). Returns the raw `Data` payload;
 * `src/bom/utils/history.js` parses it.
 */
export async function fetchItemHistory({ itemId, itemType = 'PRT' } = {}) {
  if (itemId === undefined || itemId === null || itemId === '') {
    throw new OrcanosError(0, 'itemId is required');
  }
  const params = new URLSearchParams({
    item_id: String(itemId),
    item_type: String(itemType || 'PRT'),
  });
  const body = await _request(`history?${params.toString()}`, { method: 'GET' });
  return body.data;
}

/**
 * Compare two tags on many parts at once (Compare Revisions → Show children).
 *
 * `items` is [{ id, type, keys }]; a tag of null is "Current". The server streams one
 * NDJSON line per part as it finishes — `onLine` is called with each parsed line:
 *   { type: 'start', total, cached } · { type: 'item', id, status, … } · { type: 'done' }
 * A `{ type: 'error' }` line is thrown as an OrcanosError (401 = session expired).
 * `signal` (AbortSignal) stops the read — and the server's Orcanos calls with it.
 */
export async function compareChildren({ items, fromTag, toTag, signal, onLine }) {
  let resp;
  try {
    resp = await fetch('/api/bom/compare-children', {
      method: 'POST',
      credentials: 'same-origin',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/x-ndjson',
        'X-CSRF-Token': getCsrfToken(),
      },
      body: JSON.stringify({ items, from_tag: fromTag, to_tag: toTag }),
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new OrcanosError(0, `Could not reach the server (${err.message}).`);
  }
  if (!resp.ok) {
    let detail = null;
    try { detail = (await resp.json()).detail; } catch { /* non-JSON */ }
    throw new OrcanosError(resp.status, typeof detail === 'string' ? detail : `Server error (HTTP ${resp.status}).`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  const emit = (text) => {
    if (!text.trim()) return;
    const msg = JSON.parse(text);
    if (msg.type === 'error') throw new OrcanosError(msg.status || 0, msg.detail || 'Compare failed.');
    onLine(msg);
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buf.indexOf('\n')) >= 0) {
      emit(buf.slice(0, nl));
      buf = buf.slice(nl + 1);
    }
  }
  emit(buf + decoder.decode());
}

/**
 * The Orcanos web URL for an item — the "Orcanos Link" column. Built on the SESSION's
 * Orcanos URL (settings.baseUrl comes from the server), so it always points at the same
 * tenant the data was read from.
 */
export function orcanosItemUrl({ baseUrl, versionId, type, itemId }) {
  const s = getSettings();
  const base = baseUrl ?? s.baseUrl;
  // DMS items and ECOs live in their own projects — link to the version they were read from.
  const ver = versionId ?? versionForType(type, s);
  return `${base}web/${ver}/items/view?Item=${encodeURIComponent(
    type
  )}&ItemId=${encodeURIComponent(itemId)}`;
}

// ─── Internal ─────────────────────────────────────────────────────────────

function _post(path, body) {
  return _request(path, {
    method: 'POST',
    // The session layer 403s a POST without the double-submit CSRF header.
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
    body: JSON.stringify(body),
  });
}

async function _request(path, init) {
  let resp;
  try {
    resp = await fetch(`/api/bom/${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(init.headers || {}) },
    });
  } catch (err) {
    throw new OrcanosError(0, `Could not reach the server (${err.message}).`);
  }

  let data = null;
  try {
    data = await resp.json();
  } catch {
    // Non-JSON error body — fall through to the status handling below.
  }

  if (resp.status === 401) {
    throw new OrcanosError(401, (data && data.detail) || 'Your session expired — please sign in again.');
  }
  if (!resp.ok) {
    const detail = data && (typeof data.detail === 'string' ? data.detail : null);
    throw new OrcanosError(resp.status, detail || `Server error (HTTP ${resp.status}).`);
  }
  if (!data) throw new OrcanosError(0, 'Server returned invalid JSON.');
  return data;
}

function _rows(data) {
  const objects = Array.isArray(data?.objects) ? data.objects : [];
  const total = parseInt(data?.total ?? objects.length, 10);
  return {
    rows: objects.map(_normalizeRow),
    total: Number.isFinite(total) ? total : objects.length,
  };
}

/**
 * Convert a raw Orcanos `Object` record into a friendlier row.
 *
 * The API returns each row as:
 *   { Field: [{ Name, Title, Text, Web_order, ... }, ...], Id, Type, ... }
 *
 * We keep `Field` as the source of truth for column rendering (so the UI can
 * iterate fields in `Web_order`), but also surface a few canonical accessors:
 *
 *   row.id          — Orcanos internal ID
 *   row.type        — "PRT" / "PI" / etc.
 *   row.fields      — original Field array, sorted by Web_order
 *   row.byName(n)   — text of a field by Name
 *   row.objName     — value of the Obj_name field
 *   row.userPrefix  — value of the User_Prefix (Key) field
 */
function _normalizeRow(obj) {
  const fields = Array.isArray(obj.Field) ? obj.Field : [];
  const sorted = [...fields].sort((a, b) => {
    const ao = parseInt(a.Web_order ?? a.Order ?? 0, 10);
    const bo = parseInt(b.Web_order ?? b.Order ?? 0, 10);
    return ao - bo;
  });
  // For picklist / lookup fields (Status, Severity, etc.), Orcanos puts the
  // human-readable label in `Display_text` and the internal id in `Text`.
  // For plain text fields only `Text` is populated. Prefer the display value
  // so picklists render their label rather than a numeric id.
  const fieldText = (f) =>
    (f && (f.Display_text || f.Display || f.Text || f.Value || '')) || '';
  const byName = (n) => fieldText(sorted.find((f) => f.Name === n));
  const byTitle = (t) => fieldText(sorted.find((f) => f.Title === t));

  // Orcanos's top-level Id field arrives as a string like "46584 (42195)"
  // where the parenthetical is the canonical numeric ID used for URLs.
  // Extract that. If we can't find a parenthetical (older filters / odd data),
  // fall back to whatever digits we can find in Id, then User_Prefix.
  const id = obj.Id ?? '';
  const userPrefix = byName('User_Prefix');
  const itemId =
    extractNumericId(id) ||
    extractNumericId(userPrefix) ||
    String(id).replace(/[^\d]/g, '');

  // `originalId` is the value to send as `parent_original_id` when expanding
  // this row's children:
  //   - For PRTs (top-level BOMs): same as itemId.
  //   - For PIs:  the PRT-side number embedded in `Master Part Source`,
  //               e.g. "PRT-39382-901631 description..." → "39382".
  const type = obj.Type ?? '';
  let originalId = '';
  if (type === 'PI') {
    const mps =
      byName('Master Part Source') ||
      byName('Master_Part_Source') ||
      byTitle('Master Part Source');
    const m = String(mps || '').match(/^[A-Z]+-(\d+)-/);
    if (m) originalId = m[1];
  }
  if (!originalId) originalId = itemId;

  // CS21 integer — for the Where-Used "Locate" feature. Two PIs that
  // instance the same source PRT will have the same CS21 integer. The CS21
  // column may be returned as a raw integer ("39382") or wrapped in a
  // "PRT-39382-901631 description..." display string, so we pull the first
  // run of digits either way. Empty for PRT rows (no CS21 field).
  const cs21Field = sorted.find(
    (f) =>
      f.Name === 'CS21' ||
      f.Title === 'CS21' ||
      f.Name === 'Master Part Source' ||
      f.Title === 'Master Part Source' ||
      f.Name === 'Master_Part_Source'
  );
  // Prefer the raw stored value (Text/Value) over the Display label so the
  // id we send for Where-Used matches whatever Orcanos stores in CS21 — not
  // the descriptive "PRT-… description" label.
  const cs21Raw = cs21Field
    ? cs21Field.Text ||
      cs21Field.Value ||
      cs21Field.Display_text ||
      cs21Field.Display ||
      ''
    : '';
  const cs21IntMatch = String(cs21Raw).match(/\d+/);
  const cs21Int = cs21IntMatch ? cs21IntMatch[0] : '';

  // ECO children only: the file revision this ECO released (server-resolved), see fetchEcoChildren.
  const f = obj.Eco_file;
  const ecoFile = f && !f.error && f.url
    ? { attachmentId: f.attachment_id, name: f.name, revision: f.revision, changeNo: f.change_no, url: f.url }
    : null;

  return {
    ecoFile,
    ecoFileError: f && f.error ? String(f.error) : '',
    id,           // raw Id string from Orcanos (may include parenthetical)
    itemId,       // canonical numeric ID for URL building
    originalId,   // value to use as parent_original_id when expanding children
    cs21Raw,      // raw CS21 column value (empty on PRT rows)
    cs21Int,      // first integer extracted from CS21 (empty on PRT rows)
    type,
    fields: sorted,
    byName,
    byTitle,
    objName: byName('Obj_name') || byTitle('Obj_name'),
    userPrefix,
    raw: obj,
  };
}

function extractNumericId(s) {
  if (!s) return '';
  // Prefer the parenthetical at the end if present.
  const m = String(s).match(/\((\d+)\)\s*$/);
  if (m) return m[1];
  return '';
}
